import { useState } from "react";
import { toast } from "sonner";
import { Printer, Download, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PageHeader, Loader } from "../components/common";
import { ReportSheet } from "../components/ReportSheet";
import { useApi } from "../hooks/useApi";
import { useDepot } from "../context/DepotContext";
import { api, formatApiError, todayStr, TZ } from "../lib/api";

export default function DailyReport() {
  const { depotParam, scopeLabel } = useDepot();
  const [date, setDate] = useState(todayStr());
  const [downloading, setDownloading] = useState(false);
  const params = { date, depot_id: depotParam, tz: TZ };
  const { data, loading } = useApi("/reports/daily", params);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/reports/daily/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `daily-report-${scopeLabel.replace(/\s+/g, "-").toLowerCase()}-${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="no-print">
        <PageHeader eyebrow={scopeLabel} title="Daily Report" subtitle="Evening closing sheet — print it or save as PDF after business hours."
          actions={
            <>
              <Input type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)} className="h-10 w-[160px] bg-slate-900" data-testid="report-date-input" />
              <Button onClick={() => window.print()} disabled={!data} variant="outline" className="border-slate-700" data-testid="print-report-button">
                <Printer className="h-4 w-4" /> Print
              </Button>
              <Button onClick={downloadPdf} disabled={downloading || !data} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="download-pdf-button">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
              </Button>
            </>
          } />
      </div>
      {loading && !data ? <Loader /> : data && <ReportSheet r={data} />}
    </div>
  );
}
